/**
 * What a set of probe settings does, in time (#2658 follow-up): the numbers
 * the 探测设置 drawer prints over each section. Pure; every figure is an
 * approximation of the gateway rules (docs/routing/behavior-tree.md §4 and
 * `target-probes/level.ts`) and the copy labels it 约.
 *
 * Normal / alert rounds. A round fires `shots` shots `spacing` apart; with
 * the upstream down every shot runs to its timeout, so one round takes
 * shots × timeout + (shots − 1) × spacing. A failed round below the trip line
 * makes the leg due again `confirm` seconds after it ends (never later than
 * the cadence), and never sooner than one round budget after it started. The
 * trip needs `fail_rounds` failed rounds in a row, so it lands, measured from
 * the start of the outage:
 *   fastest ≈ (fail_rounds − 1) × step + round   (outage just before a round)
 *   slowest ≈ fastest + cadence                  (outage just after one)
 *
 * War. Waves of concurrent shots every `wave_every` seconds, each shot aborted
 * at the slow line; `waves_to_trip` bad waves in a row trip, so measured from
 * the trigger it lands between (waves_to_trip − 1) × wave_every (shots failing
 * at once) and (waves_to_trip − 1) × max(wave_every, slow line) + slow line
 * (shots hanging to the slow line: a wave waits for its slowest shot, so with
 * a slow line above the wave interval the next wave starts when it ends), and
 * it only trips an upstream that stays bad for at least
 * (waves_to_trip − 1) × wave_every seconds.
 *
 * Cooldown. After the k-th war (k counts every war; it starts over only after
 * the reset window with no trigger, cooldown-blocked ones included) the leg cannot go to
 * war again for min(base × factor^(k − 1), max) minutes.
 */
import {
  guaranteedProbeRoundBudgetSeconds,
  GUARANTEED_PROBE_ALERT_FALLBACK_MULTIPLIER,
  GUARANTEED_PROBE_ALERT_FAST_MIN_FAIL_PCT,
  GUARANTEED_PROBE_ALERT_FAST_MIN_FAILURES,
  GUARANTEED_PROBE_ALERT_FAST_WINDOW_SECONDS,
  GUARANTEED_PROBE_ALERT_WINDOW_SECONDS,
  GUARANTEED_PROBE_QUALITY_MIN_SHOTS_DEFAULT,
  GUARANTEED_PROBE_QUALITY_RECOVER_MIN_SHOTS,
} from '@nadoutong/contracts';
import type { ProbeValues } from './probeSettingsModel.ts';

export interface TripWindow {
  /** Seconds from the outage (or trigger) to the trip, best and worst case. */
  readonly fastestSeconds: number;
  readonly slowestSeconds: number;
}

/** Longest a round of failing shots takes, without the settle margin. */
export function roundSeconds(values: ProbeValues): number {
  const shots = Math.max(1, values.probe_shots_per_round);
  return shots * values.probe_shot_timeout_seconds + (shots - 1) * values.probe_shot_spacing_seconds;
}

export function roundBudgetSeconds(values: ProbeValues): number {
  return guaranteedProbeRoundBudgetSeconds({
    shots: values.probe_shots_per_round,
    spacingSeconds: values.probe_shot_spacing_seconds,
    timeoutSeconds: values.probe_shot_timeout_seconds,
  });
}

function roundTrip(values: ProbeValues, cadenceSeconds: number): TripWindow {
  const round = roundSeconds(values);
  const budget = roundBudgetSeconds(values);
  const confirm = Math.min(values.probe_confirm_after_seconds, cadenceSeconds);
  const step = Math.max(round + confirm, budget);
  const rounds = Math.max(1, values.probe_trip_failed_rounds);
  const fastestSeconds = (rounds - 1) * step + round;
  return { fastestSeconds, slowestSeconds: fastestSeconds + cadenceSeconds };
}

/** 常规: rounds at the SKU interval. */
export function normalTrip(values: ProbeValues): TripWindow {
  return roundTrip(values, values.probe_interval_minutes * 60);
}

/** The alert cadence the runtime uses: never faster than one round budget. */
export function alertCadenceSeconds(values: ProbeValues): number {
  return Math.max(values.probe_alert_interval_seconds, roundBudgetSeconds(values));
}

/** 警戒: the same rounds at the alert cadence. */
export function alertTrip(values: ProbeValues): TripWindow {
  return roundTrip(values, alertCadenceSeconds(values));
}

/**
 * Real-traffic failures in the alert window that start a war: first-traffic
 * legs, fallback legs, and the share of the leg's counted attempts that must
 * fail too (red-team P2-1) — or, with the count line met, that share over
 * the last 15 s (the fast lane, #2658 tuning, constants). One upstream 4xx
 * with a server-side error type is a trigger on its own (the signature lane,
 * no numbers here). `firstByteLateSeconds`: a streamed request with no first
 * byte after that long counts one failure; null = off.
 */
export function warTriggerFailures(values: ProbeValues): {
  readonly first: number;
  readonly fallback: number;
  readonly pct: number;
  readonly windowSeconds: number;
  readonly fast: { readonly windowSeconds: number; readonly failures: number; readonly pct: number };
  readonly firstByteLateSeconds: number | null;
} {
  const late = values.probe_alert_first_byte_late_ms;
  return {
    first: values.probe_alert_min_failures,
    fallback: values.probe_alert_min_failures * GUARANTEED_PROBE_ALERT_FALLBACK_MULTIPLIER,
    pct: values.probe_alert_min_fail_pct,
    windowSeconds: GUARANTEED_PROBE_ALERT_WINDOW_SECONDS,
    fast: {
      windowSeconds: GUARANTEED_PROBE_ALERT_FAST_WINDOW_SECONDS,
      failures: GUARANTEED_PROBE_ALERT_FAST_MIN_FAILURES,
      pct: GUARANTEED_PROBE_ALERT_FAST_MIN_FAIL_PCT,
    },
    firstByteLateSeconds: late > 0 ? Math.round(late / 100) / 10 : null,
  };
}

export interface WarEffect extends TripWindow {
  /** How long the upstream must stay bad for a war to trip it. */
  readonly sustainSeconds: number;
  /** Good waves in a row that end the war early, in seconds. */
  readonly exitSeconds: number;
  /** Wall clock: no wave starts after it, except the verdict waves below. */
  readonly maxSeconds: number;
  /** Waves a war may still fire past the maximum while its last wave was bad (waves_to_trip − 1). */
  readonly verdictWavesPastMax: number;
  /** Shots one war fires at most. */
  readonly maxShots: number;
}

export function warEffect(values: ProbeValues): WarEffect {
  const every = values.probe_burst_wave_every_seconds;
  const sustainSeconds = (Math.max(1, values.probe_burst_waves_to_trip) - 1) * every;
  const slowSeconds = Math.ceil(values.probe_slow_ms / 1000);
  const maxSeconds = values.probe_burst_max_seconds;
  const hungWaveSeconds = Math.max(every, slowSeconds);
  const verdictWavesPastMax = Math.max(1, values.probe_burst_waves_to_trip) - 1;
  return {
    fastestSeconds: sustainSeconds,
    slowestSeconds: (Math.max(1, values.probe_burst_waves_to_trip) - 1) * hungWaveSeconds + slowSeconds,
    sustainSeconds,
    exitSeconds: values.probe_burst_exit_good_waves * every,
    maxSeconds,
    verdictWavesPastMax,
    // At most one wave per multiple of `every` below the maximum (fewer when
    // hung waves run late: the maximum is wall clock), plus the verdict waves.
    maxShots: (Math.ceil(maxSeconds / every) + verdictWavesPastMax) * values.probe_burst_wave_shots,
  };
}

/** Minutes the leg cannot go to war after its k-th war (k ≥ 1; see the header for how k resets). */
export function cooldownMinutes(values: ProbeValues, k: number): number {
  const base = values.probe_war_cooldown_base_minutes;
  const raw = base * values.probe_war_cooldown_factor ** (Math.max(1, k) - 1);
  return Math.min(raw, values.probe_war_cooldown_max_minutes);
}

/** Cooldowns after the 1st, 2nd, … war until the cap is reached (at most `limit`). */
export function cooldownSequence(values: ProbeValues, limit = 6): readonly number[] {
  const out: number[] = [];
  for (let k = 1; k <= limit; k += 1) {
    const minutes = cooldownMinutes(values, k);
    out.push(minutes);
    if (minutes >= values.probe_war_cooldown_max_minutes || values.probe_war_cooldown_factor === 1) break;
  }
  return out;
}

/** Shots the quality window holds at the normal cadence, and whether that reaches the minimum. */
export function qualityShotsInWindow(values: ProbeValues): { readonly shots: number; readonly judged: boolean } {
  const rounds = Math.floor(values.probe_quality_window_minutes / Math.max(1, values.probe_interval_minutes));
  const shots = rounds * values.probe_shots_per_round;
  return { shots, judged: shots >= values.probe_quality_min_shots };
}

/** Bad shots in a full window that reach the removal line and the bad-shot minimum (at least one). */
export function qualityBadShotsToTrip(values: ProbeValues, shots: number): number {
  return Math.max(1, values.probe_quality_min_bad_shots, Math.ceil((shots * values.probe_quality_trip_pct) / 100));
}

/**
 * Recovery (gateway `qualityRecoverMinShots`): the last recover-window
 * minutes must hold at least min(6, what the window holds at the normal
 * cadence) shots, at most the recover share bad, and the ok-round line.
 */
export function qualityRecoverShots(values: ProbeValues): number {
  const rounds = Math.max(1, Math.floor(values.probe_quality_recover_window_minutes / Math.max(1, values.probe_interval_minutes)));
  return Math.max(1, Math.min(GUARANTEED_PROBE_QUALITY_RECOVER_MIN_SHOTS, rounds * Math.max(1, values.probe_shots_per_round)));
}

/**
 * Minutes after an operator reset during which quality cannot remove the
 * leg: the quality signal counts only shots after the reset and judges
 * nothing below `probe_quality_min_shots` of them, so at the normal cadence
 * that is interval × ⌈min shots ÷ shots per round⌉ (faster at alert). Only
 * the round rule (consecutive failed rounds) removes the leg in that span.
 * Null when the SKU's cadence is unknown.
 */
export function qualityBlindMinutesAfterReset(row: {
  readonly probe_interval_minutes?: number | null;
  readonly probe_shots_per_round?: number | null;
  readonly probe_quality_min_shots?: number | null;
} | null): number | null {
  if (row === null) return null;
  const interval = row.probe_interval_minutes;
  const shots = row.probe_shots_per_round;
  const minShots = row.probe_quality_min_shots ?? GUARANTEED_PROBE_QUALITY_MIN_SHOTS_DEFAULT;
  const valid = (value: number | null | undefined): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value > 0;
  if (!valid(interval) || !valid(shots) || !valid(minShots)) return null;
  return interval * Math.ceil(minShots / shots);
}

/** 「2 分」 for whole minutes, else 「90 秒」: the compact duration of the summary line. */
export function compactDuration(seconds: number): { readonly unit: 'minutes' | 'seconds'; readonly value: number } {
  return seconds >= 60 && seconds % 60 === 0
    ? { unit: 'minutes', value: seconds / 60 }
    : { unit: 'seconds', value: seconds };
}

/** 「3.5」 minutes from seconds, one decimal, trailing .0 dropped. */
export function minutesText(seconds: number): string {
  const minutes = Math.round((seconds / 60) * 10) / 10;
  return Number.isInteger(minutes) ? String(minutes) : minutes.toFixed(1);
}
